<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div :class="`report-header ${pdf ? 'pdf' : ''}`">
          <!-- title -->
          <h1 class="report-title no-wrap">{{activeEnvironment.reportTitle || game.reportTitle || 'PERSONAL REPORT'}}</h1>
        </div>

        <!-- name/date -->
        <div
          :style="{
            display: 'flex',
            'flex-flow': 'row nowrap',
            'justify-content': 'space-between',
            padding: '12px 50px',
            'font-size': '1.8em',
          }"
        >
          <div class="no-wrap">
            {{username}}
          </div>
          <div class="no-wrap">
            {{date}}
          </div>
        </div>

        <!-- blue divider -->
        <div
          :style="{
            'background-color': '#05968f',
            height: '3px',
          }"
        >
        </div>

        <!-- general blurb -->
        <div
          :style="{
            padding: '12px 50px',
            'font-size': '1.35em',
            'text-align': 'justify',
            width: '63%',
          }"
        >
          Your personal report is based on demonstrated decisions and preferences that may express what comes most naturally to you.
          This can provide insights into the types of roles and experiences that will often feel most aligned and empowering to you.
          There are no good, bad, better or worse capabilities. All humans can, and do, express every capability at different times.
          Below you can learn more about the capabilities you most uniquely demonstrate in a work context.
        </div>
        <div
          :style="{
            padding: '12px 50px',
            'font-size': '1.35em',
            'font-style': 'italic',
            width: '63%',
          }"
        >
          By carefully reading the description, you will gain a clear understanding of what is meant by these high level definitions.
        </div>

        <!-- top capability -->
        <div
          style="
            display: flex;
            flex-flow: row nowrap;
            align-items: center;
            justify-content: flex-start;
            color: white;
            height: 90px;
            margin: 30px 0;
          "
          :style="{
            'background-color': topCapabilityColor,
          }"
        >
          <!-- person with capability icon -->
          <div
            :class="`person-background ${topCapabilityColorName}${pdf ? ' pdf' : ''}${(iOS() || isSafari()) ? ' broken-safari' : ''}`"
          >
            <img :class="`person-icon${pdf ? ' pdf' : ''}${(iOS() || isSafari()) ? ' broken-safari' : ''}`"
              :src="`/static/reports/archetypes/${
                topCapabilityIcon || 'Icon_Curious'
              }.png`"
            />
          </div>

          <div
            style="
              display: flex;
              flex-flow: column nowrap;
              align-items: flex-start;
              justify-content: space-between;
              height: 70%;
              position: relative;
              top: -2px;
            "
          >
            <div
              style="
                font-size: 1.35em;
              "
            >
              Your highest demonstrated capability was:
            </div>
            <div
              style="
                font-size: 2.28em;
              "
            >
              {{ topCapability }}
            </div>
          </div>
        </div>

        <!-- caveat blurb -->
        <div
          :style="{
            padding: '12px 50px',
            'font-size': '1.35em',
            'text-align': 'justify',
          }"
        >
          All personas represent a precise, fictional human being.
          A real person will have varying degrees of alignment with any specific persona or behavior.
          It is entirely probable for someone to fit in a persona and score low on any specific behavior.
          It is equally likely that an individual may have strengths in a particular behavior not from their primary capability.
        </div>

        <!-- top capability summary start -->
        <div
          :style="{
            padding: '12px 50px',
            'font-size': '1.35em',
            'background-color': topCapabilityColor,
            color: 'white',
          }"
        >
          How might this show up for you at work? {{ topCapabilitySummaryStart }}
        </div>

        <!-- top capability summary body -->
        <div
          :style="{
            padding: '12px 50px',
            'font-size': '1.35em',
          }"
        >
          {{ topCapabilitySummaryBody }}
        </div>

        <!-- behaviors header -->
        <div
          :style="{
            padding: '12px 50px',
            'font-size': '1.35em',
            'background-color': '#03a89f',
            color: 'white',
            margin: '0 0 20px',
          }"
        >
          Your highest behaviors expressed across all capabilities were:
        </div>

        <!-- behaviors -->
        <div
          v-for="(b, i) in sortedTopBehaviorSummaries"
          :key="i"
          :style="{
            display: 'flex',
            'flex-flow': 'row nowrap',
            'align-items': 'center',
            'justify-content': 'flex-start',
            padding: '2px 50px',
            margin: '0 0 20px',
            'background-color': '#e9ecef',
          }"
        >
          <div class="behavior-bullet" :class="sortedTopBehaviorColorNames[i]">
            <img :class="`behavior-bullet-icon${pdf ? ' pdf' : ''}${(iOS() || isSafari()) ? ' broken-safari' : ''}`"
              :src="`/static/reports/archetypes/${
                sortedTopBehaviorIcons[i] || 'Icon_Curious'
              }.png`"
            />
          </div>
          <div
            :style="{
              margin: '0 0 0 20px',
              'font-size': '1.35em',
              'max-width': 'calc(100% - 80px)',
            }"
          >
            {{b}}
          </div>
        </div>

        <!-- blue divider -->
        <div :style="{
          'background-color': '#05968f',
          height: '10px',
        }">
        </div>

        <!-- call to action -->
        <div
          class="call-to-action"
          :class="topCapabilityColorName"
          :style="{
            'text-align': 'justify',
            padding: '20px 50px',
            'font-size': '1.48em',
            'line-height': '130%',
            color: 'white',
            height: '370px',
          }"
        >
          <div style="white-space: pre-wrap;">{{ topCapabilityCallToAction }}</div>
        </div>

        <!-- footer -->
        <div
          style="
            background-color: black;
            color: white;
            padding: 4px 50px;
            min-height: 36px;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: flex-start;
          "
        >
          <!-- footer message (e.g. displaying ©) -->
          <div v-html="activeEnvironment.reportFooter"></div>
          <!-- footer logo -->
          <img
            v-if="activeEnvironment.reportFooterLogo"
            :src="resolveAssetPath(activeEnvironment.reportFooterLogo, 'ui/customer')"
            :class="`footer-logo ${pdf ? 'pdf' : ''}`"
            ref="footerLogo"
          >
        </div>

        <!-- chart -->
        <!-- old version: sized/placed absolute img -->
        <!-- <div
          class="chart"
          :class="topCapabilityColorName"
          style="
            height: 410px;
            width: 410px;
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            position: absolute;
            right: 50px;
            top: 130px;
          "
        ></div> -->
        <!-- FALLBACK USED ON iOS/Safari when downloading because Safari is broken (shocker) -->
        <div
          id="img"
          style="
            height: 410px;
            width: 410px;
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            position: absolute;
            right: 50px;
            top: 130px;
          "
          :style="{
            visibility: (pdf && (isSafari() || iOS())) ? undefined : 'hidden',
          }"
        ></div>
        <!-- new version: dynamic wheel with value fills, matching the chart in the dashboard -->
        <capability-wheel
          v-if="!(pdf && (isSafari() || iOS()))"
          style="
            height: 410px;
            width: 410px;
            position: absolute;
            right: 50px;
            top: 130px;
          "
          :capabilities="wheelCapabilityData"
          :disableShadow="wheelDisableShadow"
        ></capability-wheel>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import MetricsNavbar from '../ui/MetricsNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';
import CapabilityWheel from '../ui/CapabilityWheel';

export default {
  // $emit 'input' on complete
  components: {
    MetricsNavbar,
    Narrator,
    NarratorNotification,
    BottomBar,
    CapabilityWheel,
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      topCapability: 'ORGANIZATIONAL SAVVY',
      sortedTopBehaviors: ['CORPORATE SAVVY', 'GROWTH MINDSET', 'EMPATHY'],
      capabilityColors: {
        'ORGANIZATIONAL SAVVY': '#d19029',
        'ORGANIZATIONAL INNOVATION': '#ef3a36',
        EXECUTION: '#b35ca4',
        COLLABORATION: '#03a89f',
      },
      capabilityIcons: {
        'ORGANIZATIONAL SAVVY': 'Icon_Navigator',
        'FIRM PRIORITY SAVVY': 'Icon_Navigator',
        'RESOURCE SAVVY': 'Icon_Navigator',
        'ZERO SUM SAVVY': 'Icon_Navigator',
        'STAKEHOLDER SAVVY': 'Icon_Navigator',
        'CORPORATE SAVVY': 'Icon_Navigator',
        'ORGANIZATIONAL INNOVATION': 'Icon_Curious',
        'INTERNAL BELIEF': 'Icon_Curious',
        'NOVEL ORIENTED': 'Icon_Curious',
        GALVANIZER: 'Icon_Curious',
        'GROWTH MINDSET': 'Icon_Curious',
        EXECUTION: 'Icon_Driver',
        ASSURED: 'Icon_Driver',
        PROACTIVE: 'Icon_Driver',
        DECISIVE: 'Icon_Driver',
        INDEPENDENT: 'Icon_Driver',
        'EARLY ADOPTER': 'Icon_Driver',
        COLLABORATION: 'Icon_Empathizer',
        PERCEPTIVE: 'Icon_Empathizer',
        PROSOCIAL: 'Icon_Empathizer',
        EMPATHY: 'Icon_Empathizer',
        AWARENESS: 'Icon_Empathizer',
        REGULATION: 'Icon_Empathizer',
      },
      // for selecting assets
      capabilityColorNames: {
        'ORGANIZATIONAL SAVVY': 'yellow',
        'FIRM PRIORITY SAVVY': 'yellow',
        'RESOURCE SAVVY': 'yellow',
        'ZERO SUM SAVVY': 'yellow',
        'STAKEHOLDER SAVVY': 'yellow',
        'CORPORATE SAVVY': 'yellow',
        'ORGANIZATIONAL INNOVATION': 'red',
        'INTERNAL BELIEF': 'red',
        'NOVEL ORIENTED': 'red',
        GALVANIZER: 'red',
        'GROWTH MINDSET': 'red',
        EXECUTION: 'magenta',
        ASSURED: 'magenta',
        PROACTIVE: 'magenta',
        DECISIVE: 'magenta',
        INDEPENDENT: 'magenta',
        'EARLY ADOPTER': 'magenta',
        COLLABORATION: 'cyan',
        PERCEPTIVE: 'cyan',
        PROSOCIAL: 'cyan',
        EMPATHY: 'cyan',
        AWARENESS: 'cyan',
        REGULATION: 'cyan',
      },
      behaviors: {
        ASSURED: 'You express high levels of self-assurance compared to work colleagues.',
        PROACTIVE: 'You are proactive when presented with an opportunity or challenge at work.',
        DECISIVE: 'You generally make decisions and move forward confidently in the context of work.',
        INDEPENDENT: 'You have an internal understanding of the processes that enables you to be focused, creative and effective with your work.',
        'EARLY ADOPTER': 'You often onboard new ideas, technology or infrastructure  quickly due to both proactivity and self-assurance.',
        'FIRM PRIORITY SAVVY': 'You understand the common need to prioritize the greater organizational good over the needs of individuals.',
        'RESOURCE SAVVY': 'You skillfully grasp when outside help or additional resources are needed within your working group and, just as importantly, when they aren\'t needed.',
        'ZERO SUM SAVVY': 'You have an ability to find the opportunities to deliver results that can appease multiple leaders or stakeholders.',
        'STAKEHOLDER SAVVY': 'You know how to distinguish and prioritize the right stakeholders to deliver success within your work.',
        'CORPORATE SAVVY': 'You intuitively understand organizational expectations allowing you to more effortlessly thrive than most.',
        'INTERNAL BELIEF': 'You generally believe you can make a change in whatever work environment in which you exist.',
        'NOVEL ORIENTED': 'You tend to gravitate toward new, challenging or changing work projects.',
        GALVANIZER: 'You understand how to engage others to build stakeholders around new ideas at work.',
        'GROWTH MINDSET': 'You easily adopt change, growth and learning mindsets in your work.',
        PERCEPTIVE: 'You tend to deftly identify the emotional states and needs of others you work with.',
        PROSOCIAL: 'You intuitively understand and engage with cultural norms at your workplace.',
        EMPATHY: 'You uniquely feel and express empathy toward your colleagues more often than work peers.',
        AWARENESS: 'You grasp a higher than average level of self-awareness compared to your work colleagues.',
        REGULATION: 'You know the processes that help you self-regulate and maintain balance within your work life.',
      },
      capabilitySummaries: {
        'ORGANIZATIONAL SAVVY': {
          start: 'Guardians are individuals who possess an innate understanding of  how organizations function and are adept at navigating the complexities of their environments. They help organizations function. They can express many different behaviors at work, such as:',
          body: 'Having an innate organizational intelligence. Whether through experience or intuition, they know how to navigate and uphold an organization\'s culture. They know which stakeholders to prioritize when a decision needs to be made. They also know when multiple stakeholders can be appeased. They can deftly identify when hard work is required or when outside help is needed. Essentially, they help keep the organization around them efficient.',
        },
        'ORGANIZATIONAL INNOVATION': {
          start: 'Change agents are the people who drive change and innovation within organizational structures. They can express many different behaviors at work, such as:',
          body: 'They often have an internal locus of control believing they can make change. They gravitate toward projects that are new, challenging, or changing to avoid boredom. They also understand that organizational change involves building a stakeholder community, and therefore prefer to involve others in their brainstorming sessions. This kind of person will orient easily to a growth, change, and learning mindset.',
        },
        EXECUTION: {
          start: 'A Driver can express many different behaviors at work such as:',
          body: 'They can often be a person who makes choices that are self-assured. They express confidence and are comfortable working independently. They are comfortable learning new things and commonly savvy with new technology, processes or infrastructure. They also tend to be quite proactive when facing opportunities or challenges.',
        },
        COLLABORATION: {
          start: 'Collaborators are the people who empower the social structures around them. They can express many different behaviors at work, such as:',
          body: 'Being able to correctly identify the emotional states and needs of others. They are socially aware and savvy in their comprehension of social norms. They express empathy toward the limitations of others. They have high levels of self-awareness and regulation. They are people often described as having high emotional and social intelligence.',
        },
      },
      capabilityCallToAction: {
        'ORGANIZATIONAL SAVVY': 'For Guardians, a great next action step is to align yourself with stakeholders in your current role that can leverage your savvy in meaningful ways. With your intuitive abilities, nearly any leader can quickly establish trust in your capability to provide assistance within the organization you\'re a part of.\n\nIf you find yourself in any situations that doesn’t have a clear organizational culture or process, consider how you could work either by yourself or with your manager(s) to reduce that uncertainty which acts as a speed bump to your effectiveness. Moreover, looking forward at future positions, you may want to consider how clear that project, team, department or organization has laid out it’s organizational culture so you can know you’ll land in positions that maximize your capabilities.',
        'ORGANIZATIONAL INNOVATION': 'For Change Agents, a great next action step is to identify areas where your skill set is needed in your organization. By the nature of your capabilities, eventually the processes or projects you are currently changing can eventually seem mundane.\n\nKnowing this about yourself, you can get better at identifying when an effort is established and it is time for you to move on to a new project. Working with yourself, or your manager(s), to keep aligning your strengths toward projects, processes or ideas that can benefit from change in whatever roles you inhabit.',
        EXECUTION: 'For Drivers, a great next action step is to ask if your current role has clear, attainable and measurable goals to achieve. Otherwise, you may find yourself less effective and motivated than you normally are.\n\nIf you already have, great. Keep it up! If not, some approaches you can take include asking yourself or your manager how to build more clarity into your current work situation. This can also provide a guide to you as you consider all future roles.',
        COLLABORATION: 'For Collaborators, a great next action step is to consider if your current environment is social enough to leverage your capabilities. If it is, great!\n\nIf it isn’t, consider how to increase the social interaction and collaborative opportunities within your role. This could be something you do on your own, with your team or with your manager. This might be as simple as having you engaged in more work groups within your current role. Looking forward, evaluating the level of engagement required in future roles could also help in finding fulfillment and achievement along your career path.',
      },
      pdf: false,
      wheelCapabilityData: [],
      wheelDisableShadow: false,
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'challenge',
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })));
    },
    reportFileName() {
      return (this.activeClass != null && this.activeClass.studentReportFileName != null)
        ? this.activeClass.studentReportFileName
        : (this.activeEnvironment.reportTitle || this.game.reportTitle || 'Personal Report');
    },
    topCapabilitySummaryStart() {
      return (this.capabilitySummaries[this.topCapability] || {}).start || '';
    },
    topCapabilitySummaryBody() {
      return (this.capabilitySummaries[this.topCapability] || {}).body || '';
    },
    topCapabilityCallToAction() {
      return this.capabilityCallToAction[this.topCapability] || '';
    },
    topCapabilityIcon() {
      return this.capabilityIcons[this.topCapability];
    },
    topCapabilityColor() {
      return this.capabilityColors[this.topCapability];
    },
    topCapabilityColorName() {
      return this.capabilityColorNames[this.topCapability];
    },
    sortedTopBehaviorIcons() {
      return this.sortedTopBehaviors.map(n => this.capabilityIcons[n]);
    },
    sortedTopBehaviorColorNames() {
      return this.sortedTopBehaviors.map(n => this.capabilityColorNames[n]);
    },
    sortedTopBehaviorSummaries() {
      return this.sortedTopBehaviors.map(n => this.behaviors[n]);
    },
  },
  methods: {
    ...mapActions([
      'getMyGroup'
    ]),
    async show() {
      this.showNarrator = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    setStats() {
      // console.log(this.group);
      // TODO: the following order-dependent coupling is not ideal, but it currently seems
      //       to be the best way to do this given the way the data is currently organized.
      //       An ID-based coupling or a name-based coupling seems similarly dicey,
      //       and they'd just be costlier to compute.

      if (this.group.metrics.length < 26) return;

      const capabilityScores = [];
      const behaviorScores = [];

      const normalizedMetric = (metric) => {
        if (metric.max == null || metric.min == null) return metric.value;
        return (metric.value - metric.min) / Math.max(1, metric.max - metric.min);
      };

      const zScoreMetric = (metric, fallbackMean = 0, fallbackSD = 1) => {
        let mean = fallbackMean;
        let sd = fallbackSD;
        if (this.metrics) {
          const m = this.metrics[metric._id];
          if (m && m.standardDeviation) {
            mean = m.mean;
            sd = m.standardDeviation;
          }
        }
        if (sd) {
          return (metric.value - mean) / sd;
        }
        return metric.value;
      };

      behaviorScores.push({ name: 'ASSURED', value: normalizedMetric(this.group.metrics[0]) });
      behaviorScores.push({ name: 'PROACTIVE', value: normalizedMetric(this.group.metrics[1]) });
      behaviorScores.push({ name: 'DECISIVE', value: normalizedMetric(this.group.metrics[2]) });
      behaviorScores.push({ name: 'INDEPENDENT', value: normalizedMetric(this.group.metrics[3]) });
      behaviorScores.push({ name: 'EARLY ADOPTER', value: normalizedMetric(this.group.metrics[4]) });
      capabilityScores.push({ name: 'EXECUTION', value: zScoreMetric(this.group.metrics[5], 9.67, 2.74) });

      behaviorScores.push({ name: 'INTERNAL BELIEF', value: normalizedMetric(this.group.metrics[6]) });
      behaviorScores.push({ name: 'NOVEL ORIENTED', value: normalizedMetric(this.group.metrics[7]) });
      behaviorScores.push({ name: 'GALVANIZER', value: normalizedMetric(this.group.metrics[8]) });
      behaviorScores.push({ name: 'GROWTH MINDSET', value: normalizedMetric(this.group.metrics[9]) });
      capabilityScores.push({ name: 'ORGANIZATIONAL INNOVATION', value: zScoreMetric(this.group.metrics[10], 5.23, 2.25) });

      behaviorScores.push({ name: 'PERCEPTIVE', value: normalizedMetric(this.group.metrics[14]) });
      behaviorScores.push({ name: 'PROSOCIAL', value: normalizedMetric(this.group.metrics[15]) });
      behaviorScores.push({ name: 'EMPATHY', value: normalizedMetric(this.group.metrics[16]) });
      behaviorScores.push({ name: 'AWARENESS', value: normalizedMetric(this.group.metrics[17]) });
      behaviorScores.push({ name: 'REGULATION', value: normalizedMetric(this.group.metrics[18]) });
      capabilityScores.push({ name: 'COLLABORATION', value: zScoreMetric(this.group.metrics[19], 15.46, 3.53) });

      behaviorScores.push({ name: 'CORPORATE SAVVY', value: normalizedMetric(this.group.metrics[20]) });
      behaviorScores.push({ name: 'STAKEHOLDER SAVVY', value: normalizedMetric(this.group.metrics[21]) });
      behaviorScores.push({ name: 'ZERO SUM SAVVY', value: normalizedMetric(this.group.metrics[22]) });
      behaviorScores.push({ name: 'RESOURCE SAVVY', value: normalizedMetric(this.group.metrics[23]) });
      behaviorScores.push({ name: 'FIRM PRIORITY SAVVY', value: normalizedMetric(this.group.metrics[24]) });
      capabilityScores.push({ name: 'ORGANIZATIONAL SAVVY', value: zScoreMetric(this.group.metrics[25], 7.58, 3.37) });

      capabilityScores.sort((a, b) => b.value - a.value);
      this.topCapability = capabilityScores[0].name;

      // populate wheelCapabilityData
      this.wheelCapabilityData.length = 0; // clear
      const addWheelData = (capabilityName, subcapabilities) => {
        this.wheelCapabilityData.push({
          name: capabilityName,
          color: this.capabilityColors[capabilityName],
          isTop: this.topCapability === capabilityName,
          subcapabilities,
        });
      };
      // clockwise from top
      addWheelData('ORGANIZATIONAL INNOVATION', behaviorScores.slice(5, 9)); // indices 5 through 8
      addWheelData('COLLABORATION', behaviorScores.slice(9, 14)); // indices 9 through 13
      addWheelData('EXECUTION', behaviorScores.slice(0, 5)); // indices 0 through 4
      addWheelData('ORGANIZATIONAL SAVVY', behaviorScores.slice(14, 19)); // indices 14 through 18

      behaviorScores.sort((a, b) => b.value - a.value);
      this.sortedTopBehaviors = [behaviorScores[0].name, behaviorScores[1].name, behaviorScores[2].name];
    },
    iOS() {
      const platform = navigator.platform || navigator.userAgentData.platform;
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ].includes(platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    },
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the download throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      try {
        // redundant calls required to work on Safari... (https://github.com/tsayen/dom-to-image/issues/343)
        let dataUrl = await domtoimage.toPng(this.$refs.report, { cacheBust: true });
        const brokenBecauseSafari = this.iOS() || this.isSafari();
        if (brokenBecauseSafari) {
          dataUrl = await domtoimage.toPng(this.$refs.report);
          dataUrl = await domtoimage.toPng(this.$refs.report);
        }
        const doc = new JsPDF('p', 'pt', 'a4');
        doc.addImage(dataUrl, 'PNG', 28, 50, 540, 750);

        // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
        doc.save(`${this.reportFileName}.pdf`);

        this.pdf = false;
        this.showDownloading = false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to download report PDF!', error);
        try {
          this.pdf = true;
          this.selectedTab = 0;
          await Vue.nextTick();
          window.print();
          this.pdf = false;
          this.showDownloading = false;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Failed to open print dialog!', e);
          this.pdf = false;
          this.showDownloading = false;
          this.selectedTab = 0;
        }
      }
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    },
  },
  async mounted() {
    this.setStats();

    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    if (this.activeEnvironment.reportFooterLogo && this.$refs.footerLogo) {
      await new Promise((resolve, reject) => {
        if (this.$refs.footerLogo.complete) { // wait for footer logo to load
          resolve();
        } else {
          this.$refs.footerLogo.addEventListener('load', resolve);
          this.$refs.footerLogo.addEventListener('error', reject);
        }
      });
    }

    const brokenBecauseSafari = this.iOS() || this.isSafari();
    if (brokenBecauseSafari) {
      this.wheelDisableShadow = true;
      await this.$nextTick();
      const target = document.getElementById('capwheel');
      const img = document.getElementById('img');
      const svgBlob = new Blob([target.outerHTML], { type: 'image/svg+xml;charset=utf-8' });
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result;
        // console.log(base64data);
        img.style.backgroundImage = `url('${base64data}')`;
      };
      reader.readAsDataURL(svgBlob);
      this.wheelDisableShadow = false;
      await this.$nextTick();
    }

    this.showNextButton = true;
  },
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;

    // this makes background graphics show up without needing to check the box
    color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }
}

.no-wrap {
  white-space: nowrap;
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: #e1e1e1;
  color: black;
  line-height: 145%;
}
.report.pdf {
  border: solid;
  border-width: 3.5px;
  border-color: var(--primaryColor);
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 30px;
  left: 30px;
  z-index: 1;
  overflow: auto;
}

.report-header {
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  height: 60px;
  padding: 0 50px;
  background-color: black;
  color: #e1e1e1;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  z-index: 10;
}
.footer-logo {
  object-fit: contain;
  max-height: 30px;
  max-width: 30%;
  margin-bottom: 15px;
  z-index: 10;
}
.report-title {
  font-size: 1.9em;
  font-weight: normal;
  white-space: pre-wrap;
  text-align: right;
  margin: 0;
  max-width: 60%;
  max-height: 100%;
  line-height: 100%;
}

.behavior-bullet {
  width: 60px;
  height: 60px;
  background-image: url('/static/reports/bulletbg.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.behavior-bullet.red {
  background-image: url('/static/reports/bulletbg-red.png');
}

.behavior-bullet.yellow {
  background-image: url('/static/reports/bulletbg-yellow.png');
}

.behavior-bullet.magenta {
  background-image: url('/static/reports/bulletbg-magenta.png');
}

.behavior-bullet.cyan {
  background-image: url('/static/reports/bulletbg-cyan.png');
}

.behavior-bullet-icon {
  width: 59%;
  height: 59%;
  object-fit: contain;
  margin: 0 0 6px 7px;
}
.behavior-bullet-icon.pdf.broken-safari {
  margin-top: 10px;
}

.person-background {
  width: 150px;
  height: 150px;
  background-image: url('/static/reports/person.png');
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 67px 0 0 0;
  margin: 0 16px;
}
.person-background.pdf.broken-safari {
  margin-top: -32px;
}

.person-background.red {
  background-image: url('/static/reports/person-red.png');
}

.person-background.yellow {
  background-image: url('/static/reports/person-yellow.png');
}

.person-background.magenta {
  background-image: url('/static/reports/person-magenta.png');
}

.person-background.cyan {
  background-image: url('/static/reports/person-cyan.png');
}

.person-icon {
  position: relative;
  top: -8px;
  left: 4px;
  width: 60%;
  height: 60%;
  object-fit: contain;
}
.person-icon.pdf.broken-safari {
  margin-top: 16px;
}

.call-to-action {
  background-image: url('/static/reports/ResultBG.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.call-to-action.red {
  background-color: #5a2322;
  background-blend-mode: multiply;
}

.call-to-action.yellow {
  background-color: #946a29;
  background-blend-mode: multiply;
}

.call-to-action.magenta {
  background-color: #61415b;
  background-blend-mode: multiply;
}

.call-to-action.cyan {
  background-color: #10514d;
  background-blend-mode: multiply;
}

.chart.red {
  background-image: url('/static/reports/Circle_Innovation.png');
}

.chart.yellow {
  background-image: url('/static/reports/Circle_Savvy.png');
}

.chart.magenta {
  background-image: url('/static/reports/Circle_Execution.png');
}

.chart.cyan {
  background-image: url('/static/reports/Circle_Collaboration.png');
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeAlmas2Report.vue